// The New York line: what the statement's own clock faces become, in New York
// time, if the user's chosen timezone is right.
//
// ADR 0039 D9.2. The picker shows the statement's RAW printed times so the user
// can check them against their own screen -- but a raw clock face does NOT move
// when the zone selection changes, so on its own it cannot tell the user whether
// the zone they picked is the right one. This line is the part that does move:
// pick the wrong zone and the session lands at an implausible New York hour,
// which is visible at a glance.
//
// NO NEW DEPENDENCY. `apps/web` has no date library and this does not add one --
// everything here is `Intl.DateTimeFormat` with an explicit `timeZone`.
//
// NOTHING READS THE HOST TIMEZONE. Every formatter names its zone, no bare
// `new Date()` decides anything, and the tests pin exact outputs -- CI runs UTC
// and the operator's machine is Pacific, so a host-zone leak would fail on one
// of the two rather than hiding.

/** The zone the line is rendered in. */
export const NEW_YORK = "America/New_York";

/** Shape of a naive wall clock: a date and a time, with no zone attached. */
type Naive = {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
};

/** One converted clock face. */
export type NewYorkClockFace = {
  /**
   * The New York time of day, `HH:MM:SS`, 24-hour.
   *
   * `null` when the chosen zone makes this wall clock NONEXISTENT -- the
   * spring-forward gap. The caller must HIDE the line rather than show a time,
   * because both readings would be invented. Same rule as the server's
   * `localize_wall_time`, which raises rather than guessing.
   */
  readonly time: string | null;
  /**
   * The chosen zone read this wall clock TWICE (the fall-back hour), and the
   * EARLIER instant was used -- matching `localize_wall_time`'s `fold=0`. A
   * one-hour choice cannot move a time into or out of a trading day, so
   * deciding deterministically beats refusing data that is merely imprecise.
   */
  readonly ambiguous: boolean;
  /**
   * The converted time is the SAME time of day the statement printed.
   *
   * Decided on the OUTPUT, never on the zone name: `America/New_York`,
   * `America/Toronto` and `America/Detroit` all produce this, and so would any
   * future zone that happens to share New York's offsets. Keying on the name
   * would special-case one of them and quietly mishandle the rest.
   */
  readonly sameAsStatement: boolean;
};

/** Both ends of a range, converted, plus what the conversion did to it. */
export type NewYorkRange = {
  readonly earliest: NewYorkClockFace;
  readonly latest: NewYorkClockFace;
  /**
   * Whether the CONVERTED span crosses midnight.
   *
   * **Computed from the converted times, never from the server's `wrapped`.**
   * The server's flag describes the statement's own clock faces; conversion can
   * both REMOVE a wrap (a 23:50-00:10 Pacific session becomes 02:50-03:10 in New
   * York) and CREATE one (20:30-21:30 Pacific becomes 23:30-00:30). Reusing the
   * server's flag would be wrong in both directions.
   *
   * `null` when the line is hidden, since there is no converted span to describe.
   */
  readonly wrapped: boolean | null;
  /**
   * BOTH ends came out identical to the statement's own times, so the line would
   * only repeat what is already on screen and the caller shows the special copy
   * instead. Each end is compared SEPARATELY -- a range that matched at one end
   * and not the other is not "already New York times".
   */
  readonly identicalToStatement: boolean;
  /** Whether the line can be shown at all. False if either end is nonexistent. */
  readonly show: boolean;
};

const MS_PER_DAY = 86_400_000;

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  const existing = formatters.get(timeZone);
  if (existing !== undefined) return existing;
  const created = new Intl.DateTimeFormat("en-US", {
    timeZone,
    // `hourCycle: "h23"` so midnight is 00 rather than 24, which `hour12: false`
    // alone does not guarantee across engines.
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  formatters.set(timeZone, created);
  return created;
}

/** The wall clock an observer in `timeZone` reads at `utcMs`. */
function partsInZone(utcMs: number, timeZone: string): Naive {
  const parts = formatterFor(timeZone).formatToParts(new Date(utcMs));
  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((part) => part.type === type);
    if (found === undefined) {
      throw new Error(`Intl did not return a ${type} part for ${timeZone}`);
    }
    return Number(found.value);
  };
  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour: read("hour"),
    minute: read("minute"),
    second: read("second"),
  };
}

/** How far `timeZone` is from UTC at `utcMs`, in ms (local minus UTC). */
function offsetMsAt(utcMs: number, timeZone: string): number {
  const local = partsInZone(utcMs, timeZone);
  return asIfUtc(local) - utcMs;
}

/** The naive fields read as though they were UTC. Not an instant on its own. */
function asIfUtc(naive: Naive): number {
  return Date.UTC(
    naive.year,
    naive.month - 1,
    naive.day,
    naive.hour,
    naive.minute,
    naive.second,
  );
}

function sameNaive(left: Naive, right: Naive): boolean {
  return (
    left.year === right.year &&
    left.month === right.month &&
    left.day === right.day &&
    left.hour === right.hour &&
    left.minute === right.minute &&
    left.second === right.second
  );
}

type Resolved = { readonly instant: number; readonly ambiguous: boolean };

/**
 * The instant at which an observer in `timeZone` read `naive`.
 *
 * The inverse of a zone lookup, which is the hard direction: a wall clock can
 * map to two instants (fall back) or to none (spring forward).
 *
 * **Why offsets are probed a day either side.** Reading the offset only at the
 * target cannot find the ambiguous case -- for a fall-back hour both candidate
 * offsets are valid, and probing the target alone yields just one of them, so
 * the hour would look unambiguous. Sampling a day before and after brackets any
 * transition within the day, giving both offsets in play; each is then VERIFIED
 * by round-tripping, so a probe that is not a real solution is discarded.
 */
function resolveInZone(naive: Naive, timeZone: string): Resolved | null {
  const target = asIfUtc(naive);
  const offsets = new Set([
    offsetMsAt(target - MS_PER_DAY, timeZone),
    offsetMsAt(target, timeZone),
    offsetMsAt(target + MS_PER_DAY, timeZone),
  ]);

  const candidates = new Set<number>();
  for (const offset of offsets) {
    const candidate = target - offset;
    if (sameNaive(partsInZone(candidate, timeZone), naive)) {
      candidates.add(candidate);
    }
  }

  if (candidates.size === 0) return null; // nonexistent -- the spring-forward gap
  return {
    // The EARLIER instant, matching localize_wall_time's fold=0.
    instant: Math.min(...candidates),
    ambiguous: candidates.size > 1,
  };
}

const NAIVE_ISO = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/;

/**
 * Parse the server's `wall_clock`.
 *
 * **This parses `wall_clock`, NEVER `raw`.** `raw` is the statement's own string
 * in whatever format thinkorswim exported (12-hour or 24-hour, seconds or not),
 * and re-parsing it here would be a second implementation of the server
 * parser's five accepted formats, free to drift from it. `wall_clock` exists
 * precisely so no client has to.
 */
function parseWallClock(value: string): Naive | null {
  const match = NAIVE_ISO.exec(value);
  if (match === null) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6] ?? "0"),
  };
}

const pad = (value: number): string => String(value).padStart(2, "0");

function timeOfDay(naive: Naive): string {
  return `${pad(naive.hour)}:${pad(naive.minute)}:${pad(naive.second)}`;
}

function secondsOfDay(time: string): number {
  const [hour, minute, second] = time.split(":").map(Number);
  return (hour ?? 0) * 3600 + (minute ?? 0) * 60 + (second ?? 0);
}

const HIDDEN: NewYorkClockFace = { time: null, ambiguous: false, sameAsStatement: false };

/**
 * One statement clock face, as it would read in New York under `zone`.
 *
 * `wallClock` is the server's naive `wall_clock` for THIS cell, so each end of a
 * range converts using ITS OWN DATE -- which is what makes the result
 * DST-correct. A range whose two ends fall either side of a transition has two
 * different offsets, and borrowing one end's date for the other would silently
 * apply the wrong one.
 */
export function convertWallClockToNewYork(wallClock: string, zone: string): NewYorkClockFace {
  const naive = parseWallClock(wallClock);
  if (naive === null) return HIDDEN;

  const resolved = resolveInZone(naive, zone);
  if (resolved === null) return HIDDEN; // nonexistent in `zone` -- hide the line

  const inNewYork = timeOfDay(partsInZone(resolved.instant, NEW_YORK));
  return {
    time: inNewYork,
    ambiguous: resolved.ambiguous,
    sameAsStatement: inNewYork === timeOfDay(naive),
  };
}

/**
 * Both ends of a statement's range, as they would read in New York under `zone`.
 *
 * Each end is converted independently from its own `wall_clock`, and the wrap is
 * recomputed from the two converted times.
 */
export function convertRangeToNewYork(args: {
  readonly earliestWallClock: string;
  readonly latestWallClock: string;
  readonly zone: string;
}): NewYorkRange {
  const earliest = convertWallClockToNewYork(args.earliestWallClock, args.zone);
  const latest = convertWallClockToNewYork(args.latestWallClock, args.zone);

  const show = earliest.time !== null && latest.time !== null;
  return {
    earliest,
    latest,
    wrapped:
      earliest.time === null || latest.time === null
        ? null
        : secondsOfDay(earliest.time) > secondsOfDay(latest.time),
    // BOTH ends, compared separately. One matching end is a coincidence of that
    // cell's time of day, not "this statement is already in New York time".
    identicalToStatement: show && earliest.sameAsStatement && latest.sameAsStatement,
    show,
  };
}
